/**
 * An assistant message's parts → the ordered content an export should carry.
 *
 * This is where the Word export earns its keep over the built-in markdown one: markdown export is
 * `getCopyText()`, which serializes text parts and silently drops everything else — so a turn whose
 * point was a chart exports as the sentence next to it. Here a `plot_chart` call is a segment of its
 * own, in the position the reader saw it, carrying the spec the chart was drawn from (the tool-call
 * *input* — the chart is generative UI, so the spec IS the chart; see SphereAIChart).
 *
 * Pure and structurally typed: it takes the minimum shape of a part rather than assistant-ui's
 * `PartState`, so it can be tested without a runtime.
 */
import { PLOT_CHART_TOOL_NAME, parseChartSpec, type ChartSpec } from "../charts/chartSpec";

/** The minimum of an assistant message part this needs to classify it. */
export type ExportablePart = {
	readonly type: string;
	readonly text?: string;
	readonly toolName?: string;
	readonly args?: unknown;
};

export type MessageExportSegment =
	| { readonly kind: "markdown"; readonly markdown: string }
	| { readonly kind: "chart"; readonly spec: ChartSpec };

/**
 * Consecutive text parts are merged into ONE markdown segment separated by a blank line: the
 * runtime can split a single answer across parts, and parsing each in isolation would break a list
 * or table that straddles the boundary.
 *
 * Everything else is dropped — reasoning, MCP tool calls, the interactive `radio`/`select` pickers.
 * A tool call is the assistant's work, not its answer (the thread files those under the execution
 * panel for the same reason), and a picker only means something while the question is live.
 * A `plot_chart` call whose spec doesn't validate is dropped too: the thread shows an error in its
 * place, and a document is not the medium to report it in.
 */
export function collectMessageExportSegments(parts: readonly ExportablePart[]): MessageExportSegment[] {
	const segments: MessageExportSegment[] = [];

	for (const part of parts) {
		if (part.type === "text") {
			const text = part.text?.trim();
			if (!text) continue;
			const last = segments.at(-1);
			if (last?.kind === "markdown") {
				segments[segments.length - 1] = { kind: "markdown", markdown: `${last.markdown}\n\n${text}` };
			} else {
				segments.push({ kind: "markdown", markdown: text });
			}
			continue;
		}

		if (part.type === "tool-call" && part.toolName === PLOT_CHART_TOOL_NAME) {
			const parsed = parseChartSpec(part.args);
			if (parsed.ok) segments.push({ kind: "chart", spec: parsed.spec });
		}
	}

	return segments;
}
